import {isString, isUndefined} from 'lodash'

/**
 * Parses a signature value that may be a JSON string or an object.
 * Returns the parsed object, or undefined if parsing fails or value is empty.
 */
export const getParsedSignatureValue = (value: unknown) => {
  if (isString(value) && value.length > 0) {
    try {
      return JSON.parse(value)
    } catch {
      return undefined
    }
  }

  return value && typeof value === 'object' ? value : undefined
}

/**
 * Checks if a signature has a signer by examining image data, parsed value properties.
 * Returns true if there is evidence of a signature (image, signed flag, employee flag, or IDs).
 */
export const hasSignatureSigner = (signature: {
  value?: unknown
  base64?: {
    imageDataBase64?: string
  }
}): boolean => {
  const parsedValue = getParsedSignatureValue(signature.value) as
    | {
        signed?: boolean
        employee?: boolean
        applicantId?: number
        imageId?: number
      }
    | undefined

  const hasImageData = isString(signature.base64?.imageDataBase64) && signature.base64.imageDataBase64.length > 0

  return (
    hasImageData ||
    parsedValue?.signed === true ||
    parsedValue?.employee === true ||
    !isUndefined(parsedValue?.applicantId) ||
    !isUndefined(parsedValue?.imageId)
  )
}

/**
 * Checks if a signature value represents a signed state.
 * Handles boolean, string, and object forms of the signed flag.
 */
export const isSignatureSigned = (value: unknown): boolean => {
  if (value === true || value === 'true') {
    return true
  }

  if (value && typeof value === 'object' && 'signed' in value) {
    return (value as {signed?: unknown}).signed === true
  }

  return false
}
